import axios from 'axios';
import { dlog, debugEnabled, safeJson } from '../utils/debug';
import { CalculatorParams, AnalysisResponse, ScenarioInfo } from '../types/api';
import { User, SubscriptionStatus, SavedAnalysis } from '../types/user';

const API_BASE_URL = process.env.REACT_APP_API_URL || 
  (process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:8000/api');

class RentVsBuyAPI {
  private client = axios.create({
    baseURL: API_BASE_URL,
    headers: {
      'Content-Type': 'application/json',
    },
  });

  constructor() {
    // Debug interceptors (no-op unless enabled)
    if (debugEnabled) {
      this.client.interceptors.request.use((config) => {
        const sample = safeJson(config.data);
        dlog('[API Request]', config.url, {
          method: config.method,
          include_yearly_data: new URLSearchParams(String(config.url).split('?')[1]).get('include_yearly_data'),
          scenario_type: sample?.scenario_type,
          time_horizon_years: sample?.analysis_settings?.time_horizon_years,
          rent: sample?.renting_params?.current_rent_living_alone,
          rent_with_roommates: sample?.renting_params?.current_rent_with_roommates,
        });
        return config;
      });
      this.client.interceptors.response.use((res) => {
        dlog('[API Response]', res.config.url, res.status);
        return res;
      }, (err) => {
        dlog('[API Error]', err?.config?.url, err?.response?.status, err?.message);
        return Promise.reject(err);
      });
    }
  }

  // Set auth token for requests
  setAuthToken(token: string) {
    this.client.defaults.headers.common['Authorization'] = `Bearer ${token}`;
  }

  // Clear auth token
  clearAuthToken() {
    delete this.client.defaults.headers.common['Authorization'];
  }

  async analyze(params: CalculatorParams, includeYearlyData = false): Promise<AnalysisResponse> {
    const response = await this.client.post(`/analyze?include_yearly_data=${includeYearlyData}`, params);
    return response.data;
  }

  async analyzeWithBaseline(scenarioParams: CalculatorParams, baselineParams: CalculatorParams, includeYearlyData = false): Promise<AnalysisResponse> {
    // FastAPI expects multiple body params as an object with keys matching parameter names
    const response = await this.client.post(`/analyze-with-baseline?include_yearly_data=${includeYearlyData}`, {
      scenario_params: scenarioParams,
      baseline_params: baselineParams
    });
    return response.data;
  }

  async getScenarios(): Promise<ScenarioInfo[]> {
    const response = await this.client.get('/scenarios');
    return response.data;
  }

  async getDefaults(): Promise<CalculatorParams> {
    const response = await this.client.get('/defaults');
    return response.data;
  }

  async validate(params: CalculatorParams) {
    const response = await this.client.post('/validate', params);
    return response.data;
  }

  async healthCheck(): Promise<{ message: string }> {
    const response = await this.client.get('/');
    return response.data;
  }

  // User management
  async getUserProfile(token: string): Promise<User> {
    this.setAuthToken(token);
    const response = await this.client.post('/user/profile');
    return response.data;
  }

  async getSubscriptionStatus(token: string): Promise<SubscriptionStatus> {
    this.setAuthToken(token);
    const response = await this.client.get('/user/subscription');
    return response.data;
  }

  async createCheckoutSession(token: string): Promise<{ checkout_url: string }> {
    this.setAuthToken(token);
    const response = await this.client.post('/user/checkout');
    return response.data;
  }

  async createCustomerPortalSession(token: string): Promise<{ portal_url: string }> {
    this.setAuthToken(token);
    const response = await this.client.post('/user/portal');
    return response.data;
  }

  // Saved analyses
  async saveAnalysisWithScenarios(
    title: string,
    description: string,
    scenarios: any[], // SavedScenario[]
    timeHorizonYears: number
  ): Promise<SavedAnalysis> {
    const response = await this.client.post('/user/analyses', {
      title,
      description,
      scenarios,
      time_horizon_years: timeHorizonYears
    });
    return response.data;
  }

  async saveAnalysis(
    title: string,
    description: string,
    parameters: CalculatorParams,
    analysisResult?: AnalysisResponse,
    scenarioTitles?: string[]
  ): Promise<SavedAnalysis> {
    const response = await this.client.post('/user/analyses', {
      title,
      description,
      parameters,
      analysis_result: analysisResult,
      scenario_titles: scenarioTitles
    });
    return response.data;
  }

  async getUserAnalyses(): Promise<{ analyses: SavedAnalysis[] }> {
    const response = await this.client.get('/user/analyses');
    return response.data;
  }

  async getAnalysis(analysisId: string): Promise<SavedAnalysis> {
    const response = await this.client.get(`/user/analyses/${analysisId}`);
    return response.data;
  }

  async updateAnalysis(
    analysisId: string,
    title?: string,
    description?: string,
    parameters?: CalculatorParams,
    analysisResult?: AnalysisResponse
  ): Promise<SavedAnalysis> {
    const response = await this.client.put(`/user/analyses/${analysisId}`, {
      title,
      description,
      parameters,
      analysis_result: analysisResult
    });
    return response.data;
  }

  async deleteAnalysis(analysisId: string): Promise<{ message: string }> {
    const response = await this.client.delete(`/user/analyses/${analysisId}`);
    return response.data;
  }
}

export const api = new RentVsBuyAPI();
